import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { API_BASE } from './api-base';
import { parseConversationReadState } from './chat-read-state';
import { apiFetch } from './api-client';
import type { ConversationReadState } from './chats';
import type { Message } from './use-chat';
import type { TurnActivitySignal } from './turn-activity';
import type { OnboardingWire } from './value-first-onboarding';
import { useDuplexer } from './use-duplexer';
import { chatHistoryCache as cache } from './chat-session';
import { cachedHistoryWindow, keepLiveThinking, prependHistoryWindow, refreshHistoryWindow } from './chat-history-window';
import { queryClient, queryKeys } from './query-client';

export interface ChatHistoryOptions {
  enabled: boolean; owner: string | null;
  /** The message an open-at-message link (`?message=`) points at. It drives the scroll and the highlight ONLY:
   *  the window is always the conversation's LATEST page, never one read around the target — there is no
   *  separate read-only view of a past window. A target older than the page is reached by paging back
   *  (`lib/open-target-search.ts`), exactly as a reader scrolling up would. */
  targetMessageId: string | null; visible: boolean;
}
interface Window {
  messages: Message[]; olderCursor: string | null; readThroughToken: string | null;
  readState: ConversationReadState; asOf: string; turn?: TurnActivitySignal;
  /** The conversation's value-first onboarding stamp (`lib/value-first-onboarding.ts`). */
  onboarding?: OnboardingWire;
  /** Group chat Phase 10b: the caller's stars among the loaded rows (every page's, unioned) and the group's pins —
   *  the LATEST page's only (an older page answers [] and never replaces them). Absent on a private chat. */
  starredMessageIds?: string[]; pinnedMessages?: unknown[];
  /** Group chat receipts (design §21): the group's people's delivered / read positions — the LATEST page only. */
  groupReceipts?: unknown;
}
export function clearChatHistoryCache() { cache.clear(); }
const keyFor = (owner: string, id: string) => JSON.stringify([owner, id]);

/** Controls the bounded history window inside the existing chat/stream instance. */
export function useChatHistory(options: ChatHistoryOptions | undefined, id: string | null, streaming: boolean,
  messages: Message[], setMessages: Dispatch<SetStateAction<Message[]>>, setLoading: Dispatch<SetStateAction<boolean>>,
  setError: Dispatch<SetStateAction<string | null>>, setTurn: Dispatch<SetStateAction<TurnActivitySignal | null>>,
  onOnboarding?: (conversationId: string, seed: OnboardingWire | undefined) => void) {
  // Tagged with the selection it was read for: in the commit that switches conversation the previous
  // window is still in state, and its cursor must not be spent on — or counted as a read of — the new one.
  const [window, setWindow] = useState<(Window & { key: string }) | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const sequence = useRef(0);
  const active = useRef({ options, id, streaming });
  active.current = { options, id, streaming };
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const enabled = options?.enabled === true;
  const owner = options?.owner ?? null;
  const target = options?.targetMessageId ?? null;
  const key = enabled && owner && id ? keyFor(owner, id) : null;
  const currentKey = useRef(key);
  const paintedKey = useRef<string | null>(null);
  // The conversation this instance just created (see `adopt`), held until its selection lands.
  const adopted = useRef<string | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const earlier = useRef<{ key: string; cursor: string | null } | null>(null);
  currentKey.current = key;

  const fetchWindow = useCallback(async (conversationId: string, cursor?: string, signal?: AbortSignal) => queryClient.fetchQuery({
    queryKey: queryKeys.chatWindow(owner, conversationId, cursor), staleTime: 0, cacheTime: 10 * 60_000,
    queryFn: async ({ signal: querySignal }) => {
    const requestSignal = signal ?? querySignal;
    const params = new URLSearchParams({ contract: 'chats-v1', requestId: crypto.randomUUID(), limit: '40' });
    if (cursor) params.set('cursor', cursor);
    const response = await apiFetch(`${API_BASE}/api/conversations/${conversationId}/messages?${params}`, { credentials: 'include', signal: requestSignal });
    const plainRead = async () => {
      const legacy = await apiFetch(`${API_BASE}/api/conversations/${conversationId}/messages`, { credentials: 'include', signal: requestSignal });
      if (!legacy.ok) throw new Error(`Chat history request failed (${legacy.status})`);
      const data = await legacy.json() as { messages?: Message[]; turn?: TurnActivitySignal; onboarding?: OnboardingWire };
      return { messages: data.messages ?? [], turn: data.turn, onboarding: data.onboarding, olderCursor: null, readThroughToken: null, asOf: '',
        readState: { lastReadAt: null, lastReadCursor: null, hasUnreadMessages: null, hasUnreadNotifications: null,
          versions: { reader: null, messageHead: null, notifications: null } } };
    };
    if (response.status === 501 && !cursor) {
      const unsupported = await response.json().catch(() => null) as { error?: string } | null;
      if (unsupported?.error === 'chats_not_supported') return plainRead();
    }
    // The chat owner does not serve this conversation as a chat (a deactivated thread, a legacy-channel row,
    // a brief reached from /chats). It is still the user's conversation: read it plainly — latest page, no
    // read boundary — instead of failing the open. A cursor page cannot fall back:
    // its cursor was issued by the chat owner. Mirrors the native apps' `conversation_not_available` fallback.
    // The owner answers the same 404 for a conversation that is really gone; the plain read of that is
    // empty, and it stays a failure so the missing-conversation hero still shows.
    if (response.status === 404 && !cursor) {
      const refused = await response.json().catch(() => null) as { error?: string } | null;
      if (refused?.error === 'conversation_not_available') {
        const plain = await plainRead();
        if (plain.messages.length > 0) return plain;
      }
    }
    if (!response.ok) throw new Error(`Chat history request failed (${response.status})`);
    const data = await response.json() as Window;
    if (!Array.isArray(data.messages) || !data.asOf || !data.readState) throw new Error('Invalid chat history response');
    data.readState = parseConversationReadState(data.readState);
    if (cursor) data.readThroughToken = null;
    return data;
    },
  }), [owner]);

  useEffect(() => {
    const epoch = ++sequence.current;
    if (!enabled) {
      setWindow(null);
      setLoadingOlder(false);
      paintedKey.current = null;
      // The same abandonment the enabled branch does below, because this reader being off does not mean
      // nothing is painting: `useChat`'s legacy read loads whatever is selected while history is off, so a
      // thread picked here puts ITS messages on screen. Keeping the adoption through that would mark the
      // adopted conversation as already painted and hand the reader the other thread's messages under it.
      // An EMPTY selection is the route-update gap and still keeps it (see the branch below).
      if (id && adopted.current && adopted.current !== id) adopted.current = null;
      return;
    }
    const controller = new AbortController();
    const cancel = () => {
      if (id) void queryClient.cancelQueries({ queryKey: queryKeys.chatWindow(owner, id), exact: true });
      controller.abort();
    };
    setWindow(null);
    setLoadingOlder(false);
    if (!key || !id) {
      paintedKey.current = null;
      // Between create and the route update the selection is still empty, but the thread on screen is the
      // new conversation's live turn — not a blank one to clear. That turn starts BEFORE the create
      // returns (and so before `adopt`): `send()` paints the optimistic bubble and flips `streaming`,
      // which re-runs this effect, and clearing here erased the bubble until the reply's reconcile
      // repainted it. A New chat mid-stream still clears — the abort drops `streaming` and re-runs this.
      if (!adopted.current && !active.current.streaming) setMessages([]);
      setLoading(Boolean(id));
      return cancel;
    }
    // A selection that moved somewhere else entirely abandons the adoption.
    if (adopted.current && adopted.current !== id) adopted.current = null;
    // The adopted selection has landed: the handoff is over, so a later empty selection is a deliberate clear
    // (New chat mid-stream), not the route-update gap. Consumed even mid-stream for that reason.
    if (adopted.current === id) {
      adopted.current = null;
      paintedKey.current = key;
    }
    // A route update after creating this conversation must not replace an active stream.
    if (active.current.streaming || !options?.visible) return cancel;
    const cached = cache.get(key);
    const changed = paintedKey.current !== key;
    if (changed) { earlier.current = null; failedOlderCursor.current = null; }
    paintedKey.current = key;
    if (changed) setMessages(cached ?? []);
    setLoading(changed && !cached);
    setError(null);
    void fetchWindow(id, undefined, controller.signal).then((data) => {
      if (epoch !== sequence.current || currentKey.current !== key || active.current.streaming) return;
      const retained = refreshHistoryWindow(messagesRef.current, data.messages, data.olderCursor,
        earlier.current?.cursor ?? null, earlier.current?.key === key);
      if (retained.messages === data.messages) earlier.current = null;
      setMessages(changed ? retained.messages : keepLiveThinking(messagesRef.current, retained.messages));
      setWindow({ ...data, ...retained, key });
      setTurn(data.turn ?? null);
      onOnboarding?.(id, data.onboarding);
      cache.delete(key);
      cache.set(key, cachedHistoryWindow(data.messages));
      while (cache.size > 20) cache.delete(cache.keys().next().value as string);
    }).catch((error: unknown) => {
      if (!controller.signal.aborted && epoch === sequence.current) setError(error instanceof Error ? error.message : String(error));
    }).finally(() => {
      if (epoch === sequence.current) setLoading(false);
    });
    return cancel;
  }, [enabled, owner, key, id, refresh, streaming, options?.visible, fetchWindow, setMessages, setLoading, setError, setTurn, onOnboarding]);

  // A conversation this instance just CREATED is already on screen — the live turn painted it. Mark it painted
  // before the route catches up, so the selection change that follows revalidates it silently instead of
  // treating it as a fresh load: clearing the thread and showing the conversation loader under a reply the
  // reader has just watched arrive.
  //
  // Recorded by id, and recorded whatever this instance's options say, because the reader is often switched ON
  // only AFTER the turn: on `/` it waits for the conversation list to come back with the row marked
  // `SCHEDULER`, so at create time the instance is still disabled and a key built from `enabled`/`owner`
  // resolved to null — the handoff was dropped and the switch that followed read as a cold load. The id is
  // unique on its own, so it needs neither.
  const adopt = useCallback((conversationId: string) => {
    adopted.current = conversationId;
  }, []);

  const reload = useCallback(async () => {
    if (!active.current.options?.enabled || active.current.streaming) return;
    setRefresh(value => value + 1);
  }, []);
  const scheduleReload = useCallback(() => {
    const state = active.current;
    if (!state.options?.enabled || !state.options.visible || state.streaming || document.visibilityState !== 'visible') return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { void reload(); }, 600);
  }, [reload]);
  useDuplexer((entity) => { if (entity.conversationId === active.current.id) scheduleReload(); });
  useEffect(() => {
    if (!enabled || !options?.visible) return;
    const onVisible = () => { if (document.visibilityState === 'visible') scheduleReload(); };
    document.addEventListener('visibilitychange', onVisible);
    const poll = setInterval(scheduleReload, 30_000);
    return () => { document.removeEventListener('visibilitychange', onVisible); clearTimeout(timer.current); clearInterval(poll); };
  }, [enabled, options?.visible, scheduleReload]);

  // The cursor whose fetch just failed. The open-at-message hunt in `MessagesPanel` re-runs its effect
  // whenever this callback's identity changes, and `loadingOlder` flipping true→false does exactly that —
  // so a cursor the server will never serve is re-requested in a tight loop, the same defect Android's
  // scroll effect has. `force` is the reader's own "Load earlier messages" tap: a deliberate second ask
  // clears the latch, so one transient failure cannot cost a conversation its scroll-back for the session.
  // Keyed by conversation: the ref outlives any one window, so a bare cursor string could suppress
  // paging in a DIFFERENT conversation that happened to mint the same cursor. Within one conversation
  // the latch deliberately survives a `reload()` that re-derives the same cursor — that is the same
  // dead page, and re-arming on the poll would hand the loop back.
  const failedOlderCursor = useRef<{ key: string; cursor: string } | null>(null);

  const loadOlder = useCallback(async (force = false) => {
    if (!active.current.options?.enabled || !key || !id || window?.key !== key || !window.olderCursor || loadingOlder || streaming) return;
    const epoch = sequence.current;
    const cursor = window.olderCursor;
    if (force) failedOlderCursor.current = null;
    else if (failedOlderCursor.current?.key === key && failedOlderCursor.current.cursor === cursor) return;
    setLoadingOlder(true);
    try {
      const older = await fetchWindow(id, cursor);
      if (sequence.current !== epoch || currentKey.current !== key) return;
      if (older.olderCursor === cursor) throw new Error('Repeated chat history cursor');
      earlier.current = { key, cursor: older.olderCursor };
      const olderStars = (older as Partial<Window>).starredMessageIds ?? [];
      setMessages(current => {
        const merged = prependHistoryWindow(current, older.messages);
        cache.set(key, cachedHistoryWindow(merged));
        return merged;
      });
      setWindow(current => current && ({ ...current, olderCursor: older.olderCursor,
        // Phase 10b: an older page's stars join the set; its (empty) pins are never read.
        ...(olderStars.length ? { starredMessageIds: [...new Set([...(current.starredMessageIds ?? []), ...olderStars])] } : {}) }));
    } catch (error) {
      if (sequence.current === epoch) {
        failedOlderCursor.current = { key, cursor };
        setError(error instanceof Error ? error.message : String(error));
      }
    } finally { if (sequence.current === epoch) setLoadingOlder(false); }
  }, [key, id, window?.key, window?.olderCursor, loadingOlder, streaming, fetchWindow, setMessages, setError]);

  // Stream recovery uses the same bounded API but never acknowledges an unrendered response.
  const fetchLatest = useCallback(async (conversationId: string) =>
    (await fetchWindow(conversationId)).messages, [fetchWindow]);

  // `targetMessageId` is exposed ungated by `enabled`, so the legacy (chats-off) read scrolls to it too.
  // `loaded`: THIS selection's window has landed — a cached paint does not count (the open-at-target search
  // must not give up on a target the fresh read is about to bring).
  return { enabled, targetMessageId: target, loaded: enabled && !!key && window?.key === key,
    olderCursor: enabled ? window?.olderCursor ?? null : null,
    loadingOlder, loadOlder, reload, adopt, fetchLatest, readThroughToken: enabled ? window?.readThroughToken ?? null : null,
    readState: enabled ? window?.readState ?? null : null,
    // Phase 10b: what this selection's window says about the group (undefined until it lands, or on a plain read).
    groupPins: enabled && window?.key === key ? window.pinnedMessages : undefined,
    groupStarredIds: enabled && window?.key === key ? window.starredMessageIds : undefined,
    // §21: undefined until this selection's window lands; null on a private chat or an older server.
    groupReceipts: enabled && window?.key === key ? (window.groupReceipts ?? null) : undefined };
}
